import { applyBakerUserProfileFlags } from '@bakerweb/models';
import { useQuery } from '@apollo/client';
import { useIsAuthenticated } from '@azure/msal-react';
import { useMemo } from 'react';
import { GET_BAKER_USER } from './graphql/queries/getBakerUser';

interface BakerUserPermissions {
  email?: string;
  profile: string | null;
  inventory: boolean;
  loans: boolean;
  lseg: boolean;
  dailyPricing: boolean;
  reports: boolean;
  // Admin is sourced solely from the Entra BakerAdmin role (stamped onto the
  // bakerUser query); it is no longer a profile/feature-grantable flag.
  admin: boolean;
  portfolios: boolean;
  cashFlows: boolean;
  swapIQ: boolean;
  houstonTrust: boolean;
  empyreanImport: boolean;
  yieldBookMonitor: boolean;
}

interface BakerUserData {
  bakerUser: BakerUserPermissions;
}

/**
 * Gets effective permissions by combining explicit flags with profile-based flags.
 * Admin is intentionally excluded from this profile/feature computation — it is
 * sourced solely from the Entra BakerAdmin role (see `isAdmin` below).
 */
const getEffectivePermissions = (user: BakerUserPermissions | undefined): BakerUserPermissions => {
  if (!user) {
    return {
      profile: null,
      admin: false,
      inventory: false,
      loans: false,
      lseg: false,
      dailyPricing: false,
      reports: false,
      portfolios: false,
      cashFlows: false,
      swapIQ: false,
      houstonTrust: false,
      empyreanImport: false,
      yieldBookMonitor: false
    };
  }

  // Start with explicit feature flags (admin is not a feature flag).
  const baseFlags = {
    inventory: user.inventory,
    loans: user.loans,
    lseg: user.lseg,
    dailyPricing: user.dailyPricing,
    reports: user.reports,
    portfolios: user.portfolios,
    cashFlows: user.cashFlows,
    swapIQ: user.swapIQ,
    houstonTrust: user.houstonTrust,
    empyreanImport: user.empyreanImport,
    yieldBookMonitor: user.yieldBookMonitor
  };

  // Apply profile-based flags if profile exists
  const profileFlags = applyBakerUserProfileFlags(user.profile, baseFlags);

  // Return combined permissions; admin comes straight from the role-sourced flag.
  return {
    ...profileFlags,
    admin: user.admin,
    profile: user.profile
  };
};

/**
 * Requires an <MsalProvider> ancestor (the baker app is the only consumer).
 * Permissions themselves come from the Web_User DB via the bakerUser query —
 * MSAL is only consulted to skip the query until the user is signed in.
 */
export const useBakerUserPermissions = () => {
  const isAuthenticated = useIsAuthenticated();
  const { data, loading, error, refetch } = useQuery<BakerUserData>(GET_BAKER_USER, {
    skip: !isAuthenticated,
    fetchPolicy: 'cache-first'
  });

  const user = data?.bakerUser;
  const effectivePermissions = useMemo(() => getEffectivePermissions(user), [user]);

  // Admin is sourced solely from the Entra BakerAdmin role (the bakerUser.admin
  // flag); profile/feature flags no longer affect it.
  const isAdmin = useMemo(() => user?.admin ?? false, [user]);
  // Remaining permission flags (explicit + profile-based)
  const isInventory = useMemo(() => effectivePermissions.inventory ?? false, [effectivePermissions]);
  const isLoans = useMemo(() => effectivePermissions.loans ?? false, [effectivePermissions]);
  const isLseg = useMemo(() => effectivePermissions.lseg ?? false, [effectivePermissions]);
  const isDailyPricing = useMemo(() => effectivePermissions.dailyPricing ?? false, [effectivePermissions]);
  const isReports = useMemo(() => effectivePermissions.reports ?? false, [effectivePermissions]);
  const isPortfolios = useMemo(() => effectivePermissions.portfolios ?? false, [effectivePermissions]);
  const isCashFlows = useMemo(() => effectivePermissions.cashFlows ?? false, [effectivePermissions]);
  const isSwapIQ = useMemo(() => effectivePermissions.swapIQ ?? false, [effectivePermissions]);
  const isHoustonTrust = useMemo(() => effectivePermissions.houstonTrust ?? false, [effectivePermissions]);
  const isEmpyreanImport = useMemo(() => effectivePermissions.empyreanImport ?? false, [effectivePermissions]);
  const isYieldBookMonitor = useMemo(() => effectivePermissions.yieldBookMonitor ?? false, [effectivePermissions]);

  return {
    isAdmin,
    isInventory,
    isLoans,
    isLseg,
    isDailyPricing,
    isReports,
    isPortfolios,
    isCashFlows,
    isSwapIQ,
    isHoustonTrust,
    isEmpyreanImport,
    isYieldBookMonitor,
    loading,
    error,
    user,
    refetch
  };
};
